import { useUser } from '@clerk/clerk-react';
import { AccessLevel } from '../types/access';
import {
  canAccessAdmin,
  canEnterData,
  canViewDashboard,
  canViewData,
  canViewElearning,
  determineAccessLevelFromEmails,
} from '../utils/accessLevel';
import { useHospitalOhpDomains } from './useHospitalOhpDomains';

export function useAccessLevel() {
  const { user, isLoaded } = useUser();
  const { domains: hospitalDomains, isLoading: domainsLoading } = useHospitalOhpDomains();

  if (!isLoaded) {
    return {
      accessLevel: null as AccessLevel | null,
      isLoading: true,
      isAuthenticated: false,
      emailVerified: false,
      email: null as string | null,
      user: null,
      canViewDashboard: false,
      canViewElearning: false,
      canViewData: false,
      canEnterData: false,
      canAccessAdmin: false,
    };
  }

  if (!user) {
    return {
      accessLevel: null as AccessLevel | null,
      isLoading: false,
      isAuthenticated: false,
      emailVerified: false,
      email: null as string | null,
      user: null,
      canViewDashboard: false,
      canViewElearning: false,
      canViewData: false,
      canEnterData: false,
      canAccessAdmin: false,
    };
  }

  if (domainsLoading) {
    return {
      accessLevel: null as AccessLevel | null,
      isLoading: true,
      isAuthenticated: true,
      emailVerified: false,
      email: user.primaryEmailAddress?.emailAddress || null,
      user,
      canViewDashboard: true,
      canViewElearning: true,
      canViewData: false,
      canEnterData: false,
      canAccessAdmin: false,
    };
  }

  const email = user.primaryEmailAddress?.emailAddress || null;
  const emailVerified = user.primaryEmailAddress?.verification?.status === 'verified';
  const metadata = user.publicMetadata as { accessLevel?: string };
  const accessLevel = determineAccessLevelFromEmails(
    (user.emailAddresses ?? []).map((address) => ({
      address: address.emailAddress,
      verified: address.verification?.status === 'verified',
    })),
    metadata,
    hospitalDomains
  );
  const isAuthenticated = true;

  return {
    accessLevel,
    isLoading: false,
    isAuthenticated,
    emailVerified,
    email,
    user,
    canViewDashboard: canViewDashboard(isAuthenticated),
    canViewElearning: canViewElearning(isAuthenticated),
    canViewData: canViewData(accessLevel),
    canEnterData: canEnterData(accessLevel),
    canAccessAdmin: canAccessAdmin(accessLevel),
  };
}
